import * as React from "react";
import { cn } from "../../lib/utils";

const InputOTPContext = React.createContext({});

const InputOTP = React.forwardRef(
  ({ className, maxLength = 6, value = "", onChange, onComplete, disabled, children, ...props }, ref) => {
    const handleChange = (newValue) => {
      if (onChange) {
        onChange(newValue);
      }
      if (newValue.length === maxLength && onComplete) {
        onComplete(newValue);
      }
    };

    return (
      <InputOTPContext.Provider value={{ value, maxLength, onChange: handleChange, disabled }}>
        <div ref={ref} className={cn("flex items-center gap-2", className)} {...props}>
          {children}
        </div>
      </InputOTPContext.Provider>
    );
  }
);
InputOTP.displayName = "InputOTP";

const InputOTPGroup = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <div ref={ref} className={cn("flex items-center gap-1", className)} {...props}>
      {children}
    </div>
  );
});
InputOTPGroup.displayName = "InputOTPGroup";

const InputOTPSlot = React.forwardRef(({ index, className, ...props }, ref) => {
  const { value, maxLength, onChange, disabled } = React.useContext(InputOTPContext);
  const inputRef = React.useRef(null);
  const char = value[index] || "";

  const handleKeyDown = (e) => {
    if (disabled) return;

    if (e.key === "Backspace") {
      e.preventDefault();
      if (char) {
        const newValue = value.slice(0, index) + value.slice(index + 1);
        onChange(newValue);
      } else if (index > 0) {
        const prevInput = inputRef.current?.parentElement?.previousElementSibling?.querySelector("input");
        if (prevInput) {
          prevInput.focus();
          const newValue = value.slice(0, index - 1) + value.slice(index);
          onChange(newValue);
        }
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      const prevInput = inputRef.current?.parentElement?.previousElementSibling?.querySelector("input");
      if (prevInput) prevInput.focus();
    } else if (e.key === "ArrowRight" && index < maxLength - 1) {
      const nextInput = inputRef.current?.parentElement?.nextElementSibling?.querySelector("input");
      if (nextInput) nextInput.focus();
    }
  };

  const handleInput = (e) => {
    if (disabled) return;

    // Allow alphanumeric characters only, convert to uppercase
    const inputValue = e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    if (inputValue) {
      const newValue = value.slice(0, index) + inputValue[0] + value.slice(index + 1);
      onChange(newValue);

      if (index < maxLength - 1) {
        const nextInput = inputRef.current?.parentElement?.nextElementSibling?.querySelector("input");
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handlePaste = (e) => {
    if (disabled) return;

    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, maxLength);
    if (pastedData) {
      onChange(pastedData);
    }
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  return (
    <div
      ref={ref}
      className={cn(
        "relative flex h-12 w-10 items-center justify-center border border-input rounded-md bg-background text-lg transition-all",
        char && "border-ring",
        className
      )}
      {...props}
    >
      <input
        ref={inputRef}
        type="text"
        inputMode="text"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="characters"
        spellCheck="false"
        maxLength={1}
        value={char}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={handleFocus}
        disabled={disabled}
        className="absolute inset-0 w-full h-full text-center bg-transparent outline-none text-lg font-medium uppercase"
        style={{ caretColor: "transparent" }}
      />
    </div>
  );
});
InputOTPSlot.displayName = "InputOTPSlot";

const InputOTPSeparator = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <div ref={ref} className={cn("flex items-center justify-center w-4", className)} {...props}>
      <div className="w-2 h-0.5 bg-muted-foreground/30 rounded-full" />
    </div>
  );
});
InputOTPSeparator.displayName = "InputOTPSeparator";

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator };
